import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { SiteShell } from "@/components/site-shell";
import {
  Heart,
  Bookmark,
  BookmarkCheck,
  Trash2,
  PlusCircle,
  Sparkles,
  Building2,
  Leaf,
} from "lucide-react";
import { causes, institutions, formatINR } from "@/data/sample";

export const Route = createFileRoute("/donor/favorites")({
  component: Favorites,
});

const MOCK_SAVED_CAUSES = causes.slice(0, 3).map((c) => c.id);
const MOCK_SAVED_INSTS = institutions.slice(0, 2).map((i) => i.id);

function Favorites() {
  const [savedCauses, setSavedCauses] = useState<string[]>(MOCK_SAVED_CAUSES);
  const [savedInsts, setSavedInsts] = useState<string[]>(MOCK_SAVED_INSTS);

  const myCauses = causes.filter((c) => savedCauses.includes(c.id));
  const myInsts = institutions.filter((i) => savedInsts.includes(i.id));

  const removeC = (id: string) => setSavedCauses((p) => p.filter((x) => x !== id));
  const removeI = (id: string) => setSavedInsts((p) => p.filter((x) => x !== id));

  return (
    <>
      <div className="mb-7 rounded-3xl bg-white border border-border/80 p-6 sm:p-8 shadow-xs">
        <div className="flex items-start justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--saffron)]/20 px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-[var(--maroon-deep)] border border-[var(--saffron-deep)]/30">
              <Bookmark className="h-3.5 w-3.5 text-[var(--saffron-deep)]" /> Saved Causes
            </span>
            <h1 className="mt-3 font-display text-2xl font-bold text-[var(--maroon-deep)]">
              My Wishlist
            </h1>
            <p className="mt-1 text-sm text-foreground/70">
              Causes and institutions you've bookmarked for future giving.
            </p>
          </div>
          <div className="hidden sm:block rounded-2xl bg-[var(--cream-2)] p-4 text-center border border-border/60">
            <p className="font-display text-3xl font-bold text-[var(--maroon-deep)]">
              {savedCauses.length + savedInsts.length}
            </p>
            <p className="text-xs text-foreground/60 mt-0.5">Saved items</p>
          </div>
        </div>
      </div>

      {/* ── Saved Causes ── */}
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[var(--maroon-deep)] flex items-center gap-2">
            <Leaf className="h-5 w-5 text-[var(--saffron-deep)]" />
            Saved Causes ({myCauses.length})
          </h2>
          <Link
            to="/causes"
            className="text-xs font-semibold text-[var(--maroon)] hover:underline flex items-center gap-1"
          >
            <PlusCircle className="h-3.5 w-3.5" /> Browse more
          </Link>
        </div>

        {myCauses.length === 0 ? (
          <EmptyState
            icon={<Leaf className="h-8 w-8" />}
            text="No saved causes yet"
            cta="Browse causes"
            to="/causes"
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {myCauses.map((c) => (
              <div
                key={c.id}
                className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-soft)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-card)]"
              >
                <img src={c.image} alt="" className="aspect-[16/9] w-full object-cover" />
                <div className="p-4">
                  <p className="text-xs font-semibold text-[var(--saffron-deep)] uppercase tracking-wide">
                    {c.category}
                  </p>
                  <h3 className="mt-1 font-display text-base font-bold leading-snug">{c.title}</h3>
                  <div className="mt-3">
                    <div className="mb-1.5 flex justify-between text-xs text-foreground/60">
                      <span>{formatINR(c.raised)} raised</span>
                      <span>{Math.round((c.raised / c.required) * 100)}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-border/50">
                      <div
                        className="h-full rounded-full bg-[var(--maroon)]"
                        style={{
                          width: `${Math.min(100, Math.round((c.raised / c.required) * 100))}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <Link
                      to="/donate"
                      search={{ cause: c.id } as never}
                      className="flex-1 rounded-full bg-[var(--maroon)] py-2 text-center text-xs font-bold text-white hover:bg-[var(--maroon-deep)] transition"
                    >
                      <Heart className="inline mr-1 h-3.5 w-3.5" />
                      Donate
                    </Link>
                    <button
                      onClick={() => removeC(c.id)}
                      className="grid h-8 w-8 place-items-center rounded-full border border-border hover:border-red-300 hover:bg-red-50 hover:text-red-500 transition"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Saved Institutions ── */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-[var(--maroon-deep)] flex items-center gap-2">
            <Building2 className="h-5 w-5 text-[var(--maroon)]" />
            Saved Institutions ({myInsts.length})
          </h2>
          <Link
            to="/organisations"
            className="text-xs font-semibold text-[var(--maroon)] hover:underline flex items-center gap-1"
          >
            <PlusCircle className="h-3.5 w-3.5" /> Browse more
          </Link>
        </div>

        {myInsts.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-8 w-8" />}
            text="No saved institutions yet"
            cta="Browse institutions"
            to="/organisations"
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {myInsts.map((inst) => (
              <div
                key={inst.id}
                className="flex gap-4 rounded-2xl border border-border/60 bg-card p-4 shadow-[var(--shadow-soft)] transition hover:shadow-[var(--shadow-card)]"
              >
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-[var(--cream-2)] text-2xl font-bold text-[var(--maroon-deep)]">
                  {inst.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold leading-tight truncate">{inst.name}</p>
                  <p className="text-xs text-foreground/60">
                    {inst.city} · {inst.type}
                  </p>
                  <p className="mt-1 text-xs text-foreground/60">
                    {inst.animalCount} animals sheltered
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Link
                      to="/institutions/$id"
                      params={{ id: inst.id }}
                      className="rounded-full border border-[var(--maroon)]/30 px-3 py-1.5 text-xs font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)] transition"
                    >
                      View
                    </Link>
                    <Link
                      to="/donate"
                      search={{ institution: inst.id } as never}
                      className="rounded-full bg-[var(--maroon)] px-3 py-1.5 text-xs font-bold text-white hover:bg-[var(--maroon-deep)] transition"
                    >
                      Donate
                    </Link>
                    <button
                      onClick={() => removeI(inst.id)}
                      className="ml-auto grid h-7 w-7 place-items-center rounded-full border border-border hover:border-red-300 hover:bg-red-50 hover:text-red-500 transition"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── AI Suggestion strip ── */}
      <div className="mt-10 rounded-2xl border border-[var(--saffron-deep)]/20 bg-[var(--saffron)]/8 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-[var(--saffron-deep)]" />
          <p className="text-sm font-bold text-[var(--maroon-deep)]">Seva AI suggests for you</p>
        </div>
        <p className="text-xs text-foreground/70 mb-3">
          Based on your giving history, you might also like:
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            "Fodder Drive — Ahmedabad Panjrapol",
            "Bird ICU — Vadodara Bird Hospital",
            "Stray Dog Surgery Fund — Surat",
          ].map((s) => (
            <Link
              key={s}
              to="/causes"
              className="rounded-full border border-[var(--saffron-deep)]/30 bg-white px-3 py-1.5 text-xs font-medium text-[var(--maroon)] hover:bg-[var(--cream-2)] transition"
            >
              {s}
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

function EmptyState({
  icon,
  text,
  cta,
  to,
}: {
  icon: React.ReactNode;
  text: string;
  cta: string;
  to: string;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-border/60 py-14 text-center text-foreground/50">
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-muted/40">
        {icon}
      </div>
      <p className="text-sm">{text}</p>
      <Link
        to={to}
        className="mt-3 inline-block text-xs font-semibold text-[var(--maroon)] underline"
      >
        {cta}
      </Link>
    </div>
  );
}
